// Utilities for the random-offset zoom mechanic.
//
// Instead of always zooming into the center of the image, each round picks a
// random "origin" point within a configurable radius of the center. As the
// player guesses (and zoom decreases), the view smoothly pans back to center.
//
// The origin is stored as { x, y } where 0 = left/top, 0.5 = center, 1 = right/bottom.

import { INITIAL_ZOOM, MIN_ZOOM } from '../constants/games/unlimitedGame';

// ---- Configuration ----

// Maximum distance (in 0-1 normalised coordinates) the random origin can be
// from the center (0.5, 0.5).  0.25 means the point can land anywhere in a
// circle of radius 25 percentage-points ⟹ x/y stay within [0.25 … 0.75].
const MAX_OFFSET_RADIUS = 0.25;
const DEFAULT_ZOOM_BOUNDS = Object.freeze({
  initialZoom: INITIAL_ZOOM,
  minZoom: MIN_ZOOM,
});

const clamp01 = (value) => Math.min(1, Math.max(0, value));
const parseFiniteNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizeZoomBounds = (zoomBounds) => {
  if (!zoomBounds || typeof zoomBounds !== 'object') {
    return DEFAULT_ZOOM_BOUNDS;
  }

  const initialZoom = parseFiniteNumber(zoomBounds.initialZoom, DEFAULT_ZOOM_BOUNDS.initialZoom);
  const minZoom = parseFiniteNumber(zoomBounds.minZoom, DEFAULT_ZOOM_BOUNDS.minZoom);

  // Fallback to Unlimited defaults when provided bounds are invalid.
  if (initialZoom <= minZoom) {
    return DEFAULT_ZOOM_BOUNDS;
  }

  return { initialZoom, minZoom };
};

// ---- Public API ----

/**
 * Generate a random zoom origin within a circle of `MAX_OFFSET_RADIUS`
 * around the center of the image.
 *
 * Uses polar coordinates so the distribution is uniform within the circle
 * (sqrt of random distance avoids clustering in the middle).
 */
export function generateRandomZoomOrigin() {
  const angle = Math.random() * 2 * Math.PI;
  // sqrt gives uniform distribution inside the circle
  const distance = Math.sqrt(Math.random()) * MAX_OFFSET_RADIUS;

  return {
    x: 0.5 + distance * Math.cos(angle),
    y: 0.5 + distance * Math.sin(angle),
  };
}

/**
 * Compute the current focus point given the zoom origin and the current zoom
 * level.  As zoom decreases from INITIAL_ZOOM → MIN_ZOOM the focus
 * interpolates linearly from `origin` → center (0.5, 0.5).
 *
 * @param {{ x: number, y: number } | null} origin  – the random origin for
 *   this round (null/undefined falls back to center).
 * @param {number} zoomLevel – current zoom percentage (e.g. 1000 … 100).
 * @param {{ initialZoom?: number, minZoom?: number }=} zoomBounds - optional
 *   interpolation bounds. Defaults to Unlimited bounds when omitted/invalid.
 * @returns {{ x: number, y: number }}
 */
export function getInterpolatedPosition(origin, zoomLevel, zoomBounds) {
  // No origin → always center.
  if (!origin) return { x: 0.5, y: 0.5 };

  const bounds = normalizeZoomBounds(zoomBounds);
  const normalizedZoomLevel = parseFiniteNumber(zoomLevel, bounds.minZoom);

  // progress: 0 at initialZoom  →  1 at minZoom
  const range = bounds.initialZoom - bounds.minZoom;
  const progress = range > 0 ? clamp01((bounds.initialZoom - normalizedZoomLevel) / range) : 1;

  return {
    x: origin.x + (0.5 - origin.x) * progress,
    y: origin.y + (0.5 - origin.y) * progress,
  };
}

/**
 * Convert a focus point (0-1) into the CSS `transform: translate(…)` value
 * that shifts the oversized `<img>` element so the desired part of the source
 * image appears in the center of the container.
 *
 * The `<img>` is sized at `${zoomLevel}%` and centred via flexbox.  Translating
 * by `(0.5 − fx) × 100 %` (of the element's own width) achieves the shift.
 *
 * @param {{ x: number, y: number }} position – interpolated focus point.
 * @returns {string} CSS transform value, e.g. `"translate(5%, -3%)"`.
 */
export function positionToTransform(position) {
  if (!position) return 'none';

  const tx = (0.5 - position.x) * 100;
  const ty = (0.5 - position.y) * 100;

  // Avoid a pointless transform string when already centered.
  if (Math.abs(tx) < 0.01 && Math.abs(ty) < 0.01) return 'none';

  return `translate(${tx.toFixed(2)}%, ${ty.toFixed(2)}%)`;
}

/**
 * Convenience: compute the CSS transform for a given origin + zoom in one call.
 */
export function getZoomTransform(origin, zoomLevel, zoomBounds) {
  const pos = getInterpolatedPosition(origin, zoomLevel, zoomBounds);
  return positionToTransform(pos);
}

/**
 * Compute the canvas crop coordinates that replicate the in-game zoom + pan.
 *
 * Used by `zoomedImageShare.js` when drawing the zoomed crop onto a canvas.
 *
 * @param {number} squareSize  – the size of the object-fit:cover square in
 *   source-pixel coordinates (= Math.min(naturalWidth, naturalHeight)).
 * @param {number} zoomLevel   – current zoom percentage.
 * @param {{ x: number, y: number } | null} origin – random zoom origin.
 * @param {{ initialZoom?: number, minZoom?: number }=} zoomBounds - optional
 *   interpolation bounds. Defaults to Unlimited bounds when omitted/invalid.
 * @returns {{ cropX: number, cropY: number, cropSize: number }}
 */
export function getCanvasCropForZoom(squareSize, zoomLevel, origin, zoomBounds) {
  const normalizedZoomLevel = parseFiniteNumber(zoomLevel, 100);
  const z = Math.max(1, normalizedZoomLevel / 100);
  const cropSize = squareSize / z;

  const pos = getInterpolatedPosition(origin, normalizedZoomLevel, zoomBounds);

  // pos.x = 0.5 → centred; pos.x = 0 → left edge; pos.x = 1 → right edge.
  // The crop region of size `cropSize` out of `squareSize` spans from
  //   pos * (squareSize − cropSize)   to   pos * (squareSize − cropSize) + cropSize
  const cropX = (squareSize - cropSize) * pos.x;
  const cropY = (squareSize - cropSize) * pos.y;

  return { cropX, cropY, cropSize };
}
